import React, { useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Check, GripVertical, Trash2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ColorPicker } from '@/components/ui/color-picker'
import { useTranslation } from '@/hooks/useTranslation'

export interface Column {
	id: string
	name: string
	mapped_status: string
	color?: string | null
	order_index: number
}

const getStatusOptions = (t: (key: string) => string) => [
	{ value: 'New', label: t('columnRow.statusNew'), description: t('columnRow.statusNewDescription') },
	{
		value: 'Under Consideration',
		label: t('columnRow.statusUnderReview'),
		description: t('columnRow.statusUnderReviewDescription'),
	},
	{
		value: 'Planned',
		label: t('columnRow.statusPlanned'),
		description: t('columnRow.statusPlannedDescription'),
	},
	{
		value: 'In Progress',
		label: t('columnRow.statusInProgress'),
		description: t('columnRow.statusInProgressDescription'),
	},
	{ value: 'Launched', label: t('columnRow.statusLaunched'), description: t('columnRow.statusLaunchedDescription') },
	{ value: 'Declined', label: t('columnRow.statusDeclined'), description: t('columnRow.statusDeclinedDescription') },
]

interface ColumnRowProps {
	column: Column
	onUpdate: (id: string, updates: Partial<Column>) => void
	onRemove: (id: string) => void
	isSaving: boolean
}

export function ColumnRow({
	column,
	onUpdate,
	onRemove,
	isSaving,
}: ColumnRowProps) {
	const { t } = useTranslation()
	const [isEditingName, setIsEditingName] = useState(false)
	const [tempName, setTempName] = useState(column.name)
	const statusOptions = getStatusOptions(t)

	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id: column.id,
	})

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: isDragging ? 'none' : transition,
		zIndex: isDragging ? 50 : 'auto',
	}

	const handleSaveName = () => {
		if (tempName.trim() && tempName !== column.name) {
			onUpdate(column.id, { name: tempName.trim() })
		}
		setIsEditingName(false)
	}

	const handleCancelEdit = () => {
		setTempName(column.name)
		setIsEditingName(false)
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleSaveName()
		} else if (e.key === 'Escape') {
			handleCancelEdit()
		}
	}

	const handleColorChange = (color: string) => {
		onUpdate(column.id, { color })
	}

	return (
		<motion.div
			ref={setNodeRef}
			style={style}
			layout={!isDragging}
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: isDragging ? 0.7 : 1, y: 0 }}
			exit={{ opacity: 0, y: -20 }}
			className={cn(
				'group relative bg-card border border-border/50 rounded-lg p-4 hover:bg-muted/30 transition-all duration-200',
				isDragging &&
					'shadow-2xl bg-background/95 backdrop-blur-sm border-2 border-primary/30 z-50'
			)}>
			{/* Mobile Layout - Stacked */}
			<div className="flex flex-col gap-4 md:hidden">
				{/* Header Row with Drag Handle and Delete */}
				<div className="flex items-center justify-between">
					<button
						{...attributes}
						{...listeners}
						disabled={isSaving}
						className="flex items-center justify-center w-8 h-8 opacity-60 hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50 hover:bg-muted/50 rounded-md"
						aria-label={t('columnRow.dragToReorder')}>
						<GripVertical className="h-4 w-4 text-muted-foreground" />
					</button>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => onRemove(column.id)}
						disabled={isSaving}
						className="h-8 w-8 p-0 opacity-60 hover:opacity-100 transition-opacity hover:text-red-600 hover:bg-red-50"
						aria-label={t('columnRow.deleteColumn')}>
						<Trash2 className="h-4 w-4" />
					</Button>
				</div>

				{/* Column Name */}
				<div className="space-y-2">
					<label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
						{t('columnRow.columnName')}
					</label>
					{isEditingName ? (
						<div className="flex items-center gap-2">
							<Input
								value={tempName}
								onChange={(e) => setTempName(e.target.value)}
								onKeyDown={handleKeyDown}
								onBlur={handleSaveName}
								className="text-sm h-9 flex-1"
								autoFocus
							/>
							<Button
								variant="ghost"
								size="sm"
								onClick={handleSaveName}
								className="h-8 w-8 p-0 hover:bg-green-50 hover:text-green-600">
								<Check className="h-4 w-4" />
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={handleCancelEdit}
								className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600">
								<X className="h-4 w-4" />
							</Button>
						</div>
					) : (
						<button
							onClick={() => {
								setTempName(column.name)
								setIsEditingName(true)
							}}
							disabled={isSaving}
							className="w-full text-left p-2 hover:bg-muted/50 rounded-md transition-colors text-sm font-medium hover:text-primary border border-transparent hover:border-border">
							{column.name}
						</button>
					)}
				</div>

				{/* Status Mapping */}
				<div className="space-y-2">
					<label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
						{t('columnRow.mapsToStatus')}
					</label>
					<Select
						value={column.mapped_status}
						onValueChange={(value) =>
							onUpdate(column.id, { mapped_status: value })
						}
						disabled={isSaving}>
						<SelectTrigger className="h-9">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{statusOptions.map((status) => (
								<SelectItem key={status.value} value={status.value}>
									<div className="flex flex-col gap-1">
										<span className="font-medium">{status.label}</span>
									</div>
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{/* Color */}
				<div className="space-y-2">
					<label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
						{t('columnRow.color')}
					</label>
					<ColorPicker
						value={column.color || '#6B7280'}
						onChange={handleColorChange}
						className="h-9 w-full"
					/>
				</div>
			</div>

			{/* Desktop Layout - Grid */}
			<div className="hidden md:grid md:grid-cols-12 md:items-center md:gap-4">
				{/* Drag Handle */}
				<div className="col-span-1">
					<button
						{...attributes}
						{...listeners}
						disabled={isSaving}
						className="flex items-center justify-center w-8 h-8 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50 hover:bg-muted/50 rounded-md"
						aria-label={t('columnRow.dragToReorder')}>
						<GripVertical className="h-4 w-4 text-muted-foreground" />
					</button>
				</div>

				{/* Column Name */}
				<div className="col-span-3">
					{isEditingName ? (
						<div className="flex items-center gap-2">
							<Input
								value={tempName}
								onChange={(e) => setTempName(e.target.value)}
								onKeyDown={handleKeyDown}
								onBlur={handleSaveName}
								className="text-sm h-8 flex-1"
								autoFocus
							/>
							<Button
								variant="ghost"
								size="sm"
								onClick={handleSaveName}
								className="h-7 w-7 p-0 hover:bg-green-50 hover:text-green-600">
								<Check className="h-3 w-3" />
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={handleCancelEdit}
								className="h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600">
								<X className="h-3 w-3" />
							</Button>
						</div>
					) : (
						<button
							onClick={() => {
								setTempName(column.name)
								setIsEditingName(true)
							}}
							disabled={isSaving}
							className="text-left hover:text-primary transition-colors text-sm font-medium group-hover:underline">
							{column.name}
						</button>
					)}
				</div>

				{/* Status Mapping */}
				<div className="col-span-3">
					<Select
						value={column.mapped_status}
						onValueChange={(value) =>
							onUpdate(column.id, { mapped_status: value })
						}
						disabled={isSaving}>
						<SelectTrigger className="h-8 text-xs">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{statusOptions.map((status) => (
								<SelectItem key={status.value} value={status.value}>
									<div className="flex flex-col gap-1">
										<span className="font-medium">{status.label}</span>
									</div>
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{/* Color */}
				<div className="col-span-4">
					<ColorPicker
						value={column.color || '#6B7280'}
						onChange={handleColorChange}
						className="h-8 w-full max-w-[120px]"
					/>
				</div>

				{/* Actions */}
				<div className="col-span-1 flex justify-center">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => onRemove(column.id)}
						disabled={isSaving}
						className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-600 hover:bg-red-50"
						aria-label={t('columnRow.deleteColumn')}>
						<Trash2 className="h-3 w-3" />
					</Button>
				</div>
			</div>
		</motion.div>
	)
}
